<script setup lang="ts">
import {CalendarDays} from "@lucide/vue";
import DosageFormSeparator from "@/dosage/components/DosageFormSeparator.vue";
import DosageOptionChip from "@/dosage/components/DosageOptionChip.vue";
import DatePicker from "@/core/components/date-picker/DatePicker.vue";
import {computed, Ref} from "vue";
import {CalendarDate, DateValue} from "@internationalized/date";

interface Selection {
    label: string;
    start: Date | null;
    end: Date | null;
}

const props = defineProps<{
    initialDate?: Date | null;
    startError?: string | null;
    endError?: string | null;
}>();

const start = defineModel<Date | null>("start", {required: true});
const end = defineModel<Date | null>("end", {required: true});

/**
 * Define predefined duration configurations
 */
const durationConfigs = [
    {label: 'Indéfinie', type: 'undefined'},
    {label: '7 jours', type: 'days', value: 7},
    {label: '10 jours', type: 'days', value: 10},
    {label: '14 jours', type: 'days', value: 14},
    {label: '21 jours', type: 'days', value: 21},
    {label: '28 jours', type: 'days', value: 28},
    {label: '3 mois', type: 'months', value: 3},
    {label: '6 mois', type: 'months', value: 6},
    {label: '12 mois', type: 'months', value: 12},
] as const;

/**
 * Get the base date for calculations.
 * Uses start.value if set, otherwise props.initialDate, otherwise today date
 */
const getBaseDate = (): Date => {
    let baseDate: Date;

    if (start.value) {
        baseDate = new Date(start.value);
    } else if (props.initialDate) {
        baseDate = new Date(props.initialDate);
    } else {
        baseDate = new Date();
    }

    baseDate.setHours(0, 0, 0, 0);

    return baseDate;
};

/**
 * Calculate end date based on duration type and value
 */
const calculateEndDate = (baseDate: Date, type: string, value: number): Date => {
    const endDate = new Date(baseDate);

    switch (type) {
        case 'days':
            return new Date(baseDate.getTime() + value * 24 * 60 * 60 * 1000);
        case 'months':
            endDate.setMonth(endDate.getMonth() + value);
            return endDate;
        default:
            return endDate;
    }
};

/**
 * Generate predefined selections based on configurations
 */
const predefinedSelections = computed((): Selection[] => {
    const baseDate = getBaseDate();

    return durationConfigs.map(config => {
        if (config.type === 'undefined') {
            return {label: config.label, start: null, end: null};
        }

        const startDate = new Date(baseDate);
        const endDate = calculateEndDate(baseDate, config.type, config.value);

        return {
            label: config.label,
            start: startDate,
            end: endDate
        };
    });
});

/**
 * Handle predefined selection click
 */
const handleSelection = (selection: Selection) => {
    start.value = selection.start;
    end.value = selection.end;
};

/**
 * Check if a selection matches the current start and end dates
 */
const isSelected = (selection: Selection) => {
    return isSameDate(start.value, selection.start) && isSameDate(end.value, selection.end);
};

/**
 * Compare two dates ignoring time components
 */
const isSameDate = (date1: Date | null, date2: Date | null): boolean => {
    if (date1 === null && date2 === null) return true;
    if (date1 === null || date2 === null) return false;

    return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
};

/**
 * Create a computed property that maps between a Ref<Date | null> and DateValue | null for DatePicker
 */
const createDateComputed = (dateRef: Ref<Date | null>) => {
    return computed({
        get: (): DateValue | undefined => {
            if (!dateRef.value) return undefined;

            return new CalendarDate(
                dateRef.value.getFullYear(),
                dateRef.value.getMonth() + 1,
                dateRef.value.getDate()
            );
        },
        set: (value: DateValue | null) => {
            if (!value) {
                dateRef.value = null;
                return;
            }

            const jsDate = new Date(value.year, value.month - 1, value.day);
            jsDate.setHours(0, 0, 0, 0);

            dateRef.value = jsDate;
        }
    });
};

const startValue = createDateComputed(start);
const endValue = createDateComputed(end);
</script>

<template>
  <div class="space-y-4">
    <div class="flex flex-col sm:flex-row gap-3 sm:gap-2 sm:items-center flex-wrap">
      <CalendarDays class="size-4 text-gray-500" />
      <span class="text-sm font-semibold text-gray-700">Durée du traitement</span>
      <DosageFormSeparator class="hidden sm:block" />
      <div class="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
        <span>Du</span>
        <DatePicker v-model:model-value="startValue" trigger-class="w-full sm:w-auto text-primary font-medium" />
        <span>jusqu'au</span>
        <DatePicker v-model:model-value="endValue" trigger-class="w-full sm:w-auto text-primary font-medium" />
      </div>
    </div>
    <div class="flex gap-3 flex-wrap">
      <DosageOptionChip
        v-for="option in predefinedSelections"
        :key="option.label"
        :selected="isSelected(option)"
        @click="handleSelection(option)"
      >
        {{ option.label }}
      </DosageOptionChip>
    </div>
    <div v-if="startError || endError" class="flex flex-col gap-0.5">
      <p v-if="startError" class="text-xs text-red-500">
        {{ startError }}
      </p>
      <p v-if="endError" class="text-xs text-red-500">
        {{ endError }}
      </p>
    </div>
  </div>
</template>

<style scoped>

</style>
